import type { CSSProperties } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { ArrowRight } from "lucide-react";
import { toIsoDate } from "@training/shared";
import type { RecentRecord, Session } from "@training/shared";
import * as api from "../lib/api.ts";
import {
  dayLabel,
  formatMargin,
  greeting,
  lastDone,
  progressedMovements,
  sessionFigures,
  sinceVisit,
  sinceVisitLabel,
  visitHeadline,
  weekSessionsLabel,
  whenLabel,
} from "../lib/home.ts";
import { hueClass, nextTemplate } from "../lib/hue.ts";
import { breakableName, format, formatDate } from "../lib/session.ts";
import { useHue } from "../hooks/useHue.ts";
import { journalFigures } from "../lib/journal.ts";
import { dayTrends, trendRange } from "../lib/wearable.ts";
import { TodayWearable } from "../components/TodayWearable.tsx";
import { Insights } from "../components/Insight.tsx";
import { resumePath } from "../lib/resume.ts";
import { fromHere } from "../lib/back.ts";
import { Tortank } from "../components/Tortank.tsx";
import { CoachedBadge } from "../components/Avatar.tsx";

/**
 * Where the app opens, in the order one looks: what to do now, where the
 * week stands in one line, how the last session went, then the week's
 * records when there are some. Everything else has its
 * own place: the journal, progress. The page wears the colour of what is
 * next, so it is not the same on a back week as on a leg day.
 */
export function Home() {
  const block = useQuery({ queryKey: ["block"], queryFn: api.getCurrentBlock });
  const cycle = useQuery({ queryKey: ["cycle"], queryFn: api.getCycle });
  const session = useQuery({ queryKey: ["session", "current"], queryFn: api.getCurrentSession });
  const records = useQuery({ queryKey: ["records", "recent"], queryFn: api.getRecentRecords });
  const recent = useQuery({ queryKey: ["sessions", "recent"], queryFn: () => api.getSessions({ limit: 10 }) });
  const account = useQuery({ queryKey: ["account"], queryFn: api.getAccount });
  const journal = useQuery({ queryKey: ["journal", null], queryFn: () => api.getJournal() });
  // The month behind today: each figure is read against it.
  const day = toIsoDate();
  const wearable = useQuery({
    queryKey: ["wearable", day],
    queryFn: () => {
      const { from, to } = trendRange(day);
      return api.getWearableDays(from, to);
    },
  });
  const trends = dayTrends(wearable.data?.days ?? [], day);
  const coach = account.data?.user.role === "coach";
  // Once per visit and no more: the server tells a new visit from a reload.
  const visit = useQuery({ queryKey: ["visit"], queryFn: api.recordVisit, enabled: coach });
  const news = coach && visit.data && journal.data ? sinceVisit(journal.data.sessions, visit.data.since) : null;

  const current = block.data?.block ?? null;
  const today = session.data?.session ?? null;
  const running = today?.status === "in_progress" ? today : null;
  const last = lastDone(recent.data ?? []);
  const templates = cycle.data?.cycle?.templates ?? [];
  // The session under way, else the one the week still owes.
  const next =
    templates.find((template) => template.id === running?.templateId) ?? nextTemplate(cycle.data?.cycle ?? null);

  useHue(next?.position ?? null);

  // Back to the session under way, when one is: the way in otherwise is the
  // Démarrer button, there on every screen. The "À faire" strip it replaced
  // took the top of the page from the day's own figures.
  const strip = running ? (
    <NextStrip to={resumePath(running.id)} hue={next?.position ?? null} lead="En cours" name={running.name} />
  ) : null;

  return (
    <div className="page flex min-h-[calc(100dvh-5rem)] flex-col max-md:pt-4 md:min-h-dvh">
      <header className="max-md:safe-top">
        {/* Kept to one line, it clips what overflows: the room under the
            tight line height gives the "j" of "Bonjour" back its tail. */}
        <h1 className="display -mb-[0.15em] truncate pb-[0.15em] text-[2.2rem] font-bold md:text-[3rem]">
          {greeting(account.data?.user.firstName)}
        </h1>
        <p className="mt-1 text-[0.9rem] text-ink-soft first-letter:uppercase">{formatDate(toIsoDate())}</p>
      </header>

      {/* The coach opens the app to learn what happened while away: that first. */}
      {news && visit.data && <SinceVisit sessions={news} since={visit.data.since} />}
      {/* Nothing said about the session to do before knowing whether one is under way. */}
      {(strip || trends.length > 0) && (
        <div
          className={[
            "mt-5 grid gap-3",
            strip && trends.length > 0 ? "lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]" : "",
          ].join(" ")}
        >
          {strip}
          {/* The body's day beside the session under way, read first. */}
          {trends.length > 0 && <TodayWearable trends={trends} className="lg:order-first" />}
        </div>
      )}
      {current ? <WeekLine block={current} /> : block.isPending && <WeekSkeleton />}
      <Insights scope="home" className="mt-3" />

      <div className="mt-3 grid gap-3 lg:grid-cols-2">
        {/* Claude's comment is for the athlete: the coach makes their own
            reading. Nor twice the session the news above already tells. */}
        {recent.isPending ? (
          <CardSkeleton />
        ) : (
          last &&
          !news?.some((entry) => entry.id === last.id) && (
            <LastSession session={coach ? { ...last, summary: null } : last} />
          )
        )}
        <Records records={records.data?.records ?? []} />
      </div>

      <Tortank className="mt-auto pt-10" />
    </div>
  );
}

/**
 * The last session done, told rather than tabled: Claude's comment when
 * there is one, else what went up against the time before, then a handful
 * of figures. With neither to tell, it shrinks to its name and figures: the
 * big card for one line said nothing. The full detail is one tap away.
 */
function LastSession({ session }: { session: Session }) {
  const detail = useQuery({
    queryKey: ["session", session.id],
    queryFn: () => api.getSessionDetail(session.id),
  });
  const figures = detail.data ? sessionFigures(detail.data) : [];
  const progressed = !session.summary && detail.data ? progressedMovements(detail.data) : [];
  const told = Boolean(session.summary) || progressed.length > 0;

  return (
    <Link
      to={`/seance/${session.id}`}
      state={fromHere("/")}
      className={[
        "card animate-rise flex flex-col active:bg-surface-high",
        told ? "p-6" : "px-5 py-4 lg:self-start",
      ].join(" ")}
      style={{ "--i": 0 } as CSSProperties}
    >
      <span className="flex items-center justify-between gap-3">
        <span className="label first-letter:uppercase">Dernière séance · {whenLabel(session.date)}</span>
        {session.withCoach && <CoachedBadge />}
      </span>
      <span
        className={[
          "display ink-hue leading-tight font-bold",
          told ? "mt-2 text-[1.9rem] md:text-[2.2rem]" : "mt-1 text-[1.4rem]",
        ].join(" ")}
      >
        {breakableName(session.name)}
      </span>
      {session.summary && <p className="mt-3 text-[1rem] leading-relaxed text-ink">{session.summary}</p>}
      {progressed.length > 0 && (
        <ul className="mt-3 space-y-1 text-[0.92rem]">
          {progressed.slice(0, 4).map((movement) => (
            <li key={movement.name} className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 truncate">{movement.name}</span>
              <span className="num shrink-0 font-semibold text-gain">{movement.gap}</span>
            </li>
          ))}
        </ul>
      )}
      {figures.length > 0 && (
        <p className={["num mt-auto text-[0.88rem] text-ink-soft", told ? "pt-4" : "pt-1.5"].join(" ")}>
          {figures.join(" · ")}
        </p>
      )}
    </Link>
  );
}

/**
 * For the coach: what was done since the last visit, in one line, then the
 * sessions done alone — nobody saw those, so each says what went up.
 */
function SinceVisit({ sessions, since }: { sessions: api.JournalEntry[]; since: string | null }) {
  const alone = sessions.filter((entry) => !entry.withCoach);
  return (
    <section className="card animate-rise mt-5 p-5" aria-label="Depuis votre dernière visite">
      <p className="label first-letter:uppercase">{sinceVisitLabel(since)}</p>
      <p className="display mt-1.5 text-[1.6rem] leading-tight font-bold">{visitHeadline(sessions)}</p>
      {alone.length > 0 && (
        <ul className="mt-3 divide-y divide-line/60">
          {alone.map((entry) => (
            <li key={entry.id}>
              <AloneSession entry={entry} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function AloneSession({ entry }: { entry: api.JournalEntry }) {
  const detail = useQuery({ queryKey: ["session", entry.id], queryFn: () => api.getSessionDetail(entry.id) });
  const progressed = detail.data ? progressedMovements(detail.data) : [];
  return (
    <Link
      to={`/seance/${entry.id}`}
      state={fromHere("/")}
      className="-mx-2 block rounded-md px-2 py-3 active:bg-surface-high"
    >
      <span className="flex items-baseline justify-between gap-3">
        <span className="min-w-0 truncate text-[1rem] font-semibold">{entry.name}</span>
        <span className="shrink-0 text-[0.84rem] text-ink-mute first-letter:uppercase">{whenLabel(entry.date)}</span>
      </span>
      <span className="num mt-0.5 block text-[0.84rem] text-ink-soft">{journalFigures(entry)}</span>
      {progressed.length > 0 && (
        <span className="mt-1.5 block space-y-0.5 text-[0.88rem]">
          {progressed.slice(0, 3).map((movement) => (
            <span key={movement.name} className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 truncate">{movement.name}</span>
              <span className="num shrink-0 font-semibold text-gain">{movement.gap}</span>
            </span>
          ))}
        </span>
      )}
    </Link>
  );
}

/** The session to do: one line, the colour of the session, a tap away. */
function NextStrip({
  to,
  onClick,
  hue,
  lead,
  name,
  detail,
}: {
  to?: string;
  onClick?: () => void;
  hue: number | null;
  lead: string;
  name: string;
  detail?: string;
}) {
  const className = [
    "animate-rise slab flex w-full items-center gap-3 rounded-card px-5 py-4 text-left",
    hue === null ? "" : hueClass(hue),
  ].join(" ");
  const body = (
    <>
      <span className="min-w-0 flex-1">
        <span className="block text-[0.72rem] font-bold tracking-[0.1em] uppercase opacity-70">{lead}</span>
        <span className="wide block truncate text-[1.15rem] font-bold">{name}</span>
        {detail && <span className="num block truncate text-[0.82rem] opacity-70">{detail}</span>}
      </span>
      <ArrowRight size={20} strokeWidth={2.5} className="shrink-0" aria-hidden />
    </>
  );
  return to ? (
    <Link to={to} aria-label={`${lead} · ${name}`} className={className}>
      {body}
    </Link>
  ) : (
    <button type="button" onClick={onClick} aria-label={`${lead} · ${name}`} className={className}>
      {body}
    </button>
  );
}

/**
 * Where the block stands, in one line: the week, its sessions, a mark per
 * planned session of each week. The big figure it used to be is the
 * Progrès screen's business, not the first thing to read every day. Where
 * the sidebar shows the block (lg), it is not said twice.
 */
function WeekLine({ block }: { block: api.Block }) {
  const thisWeek = block.attendance[block.weekIndex - 1] ?? { done: 0, planned: block.sessionsPerWeek };
  return (
    <section className="mt-3 px-1 lg:hidden" aria-label="Semaine">
      <p className="num flex items-baseline justify-between gap-3 text-[0.88rem]">
        <span className="font-semibold">
          Semaine {block.weekIndex} sur {block.weeks}
        </span>
        <span className="text-ink-soft">{weekSessionsLabel(thisWeek.done, thisWeek.planned)}</span>
      </p>
      <div className="mt-2 flex gap-1.5">
        {block.attendance.map((week, index) => (
          <div key={week.week} className="flex min-w-0 flex-1 gap-0.5">
            {/* One mark per planned session, and one more for each extra
                session actually done, so a bonus workout still shows. */}
            {Array.from({ length: Math.max(week.planned, week.done) }, (_, mark) => (
              <span
                key={mark}
                className={[
                  "h-1.5 flex-1 rounded-[2px]",
                  mark < week.done ? "bg-ink" : "bg-line",
                  index + 1 === block.weekIndex ? "" : "opacity-60",
                ].join(" ")}
              />
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

/*
 * The place of what has not come in yet, at its size, so the page does not
 * jump as the answers land. Still rather than pulsing: on a first opening
 * only, the home screen's last answers are kept on the device after that.
 */

/** A line of text not there yet: a bar on the line box the text will have. */
function Line({ className, width }: { className: string; width: string }) {
  return (
    <span className={["block", className].join(" ")}>
      <span className={["inline-block h-[0.7em] rounded-[3px] bg-line/70 align-middle", width].join(" ")} />
      {"\u00a0"}
    </span>
  );
}

function WeekSkeleton() {
  return (
    <div aria-hidden data-skeleton="week" className="mt-3 px-1 lg:hidden">
      <Line className="text-[0.88rem]" width="w-28" />
      <div className="mt-2 flex gap-1.5">
        {Array.from({ length: 6 }, (_, week) => (
          <span key={week} className="h-1.5 flex-1 rounded-[2px] bg-line opacity-60" />
        ))}
      </div>
    </div>
  );
}

function CardSkeleton() {
  return (
    <div aria-hidden data-skeleton="card" className="card px-5 py-4 lg:self-start">
      <Line className="label" width="w-36" />
      <Line className="display mt-1 text-[1.4rem] leading-tight font-bold" width="w-32" />
      <Line className="num pt-1.5 text-[0.88rem]" width="w-48" />
    </div>
  );
}

/** As many as the screen takes without the session to do losing the lead. */
const RECORDS_SHOWN = 3;

/**
 * What was beaten in the last seven days, the latest first. Nothing at all on
 * a week without a record: an empty card would only say "no progress". The
 * muscles left alone are said in "Démarrer", where they decide something.
 */
function Records({ records }: { records: RecentRecord[] }) {
  if (records.length === 0) return null;
  const more = records.length - RECORDS_SHOWN;

  return (
    <section className="card animate-rise p-5" style={{ "--i": 2 } as CSSProperties}>
      <h2 className="label">Records · 7 jours</h2>
      {/* One column on a PC too: the card is half the screen there, and a
          third of that cuts the names and breaks the figures. */}
      <ul className="mt-3 grid gap-3">
        {records.slice(0, RECORDS_SHOWN).map((record) => (
          <li key={`${record.exerciseId}-${record.date}`} className="min-w-0">
            <Link to="/progression" state={{ exerciseId: record.exerciseId }} className="block active:opacity-70">
              <span className="flex items-baseline justify-between gap-3">
                <span className="min-w-0 truncate text-[0.85rem] text-ink-soft">{record.name}</span>
                <span className="shrink-0 text-[0.78rem] text-ink-mute">{dayLabel(record.date)}</span>
              </span>
              <span className="mt-0.5 flex items-baseline gap-2.5">
                <span className="display text-[1.35rem] font-bold">
                  {format(record.weightKg)} kg × {record.reps}
                </span>
                <span className="num text-[0.82rem] font-bold text-gain">{formatMargin(record.margin)}</span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
      {more > 0 && (
        <p className="num mt-3 text-[0.78rem] text-ink-mute">
          et {more} autre{more > 1 ? "s" : ""}
        </p>
      )}
    </section>
  );
}
